/*
 * Collar 6 页面通用交互脚本
 * 包含三个类：
 *   Collar6Reveal  —— 滚动入场淡入上移
 *   Collar6CountUp —— 数字滚动计数
 *   Collar6Faq     —— FAQ 手风琴（单开互斥）
 * 全部使用原生 JS，无依赖。
 */
(function () {
  'use strict';

  // 同一页面可能被多个 section 引入，重复执行会造成重复绑定，这里做一次全局守卫
  if (window.__collar6MotionInited) return;
  window.__collar6MotionInited = true;

  /**
   * 滚动入场动画。
   * 用一个共享的 IntersectionObserver 观察全页 [data-collar-6-reveal] 元素，
   * 进入视口后加 .is-visible 并立即 unobserve，避免重复触发与持续计算。
   */
  class Collar6Reveal {
    constructor() {
      this.observer = null;
      this.selector = '[data-collar-6-reveal]';
    }

    init() {
      // 不支持 IntersectionObserver 时直接全部显示，保证内容可见
      if (!('IntersectionObserver' in window)) {
        this.showAll();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries, observer) => {
          for (let i = 0; i < entries.length; i += 1) {
            const entry = entries[i];
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        },
        { threshold: 0.15 }
      );

      this.observeAll();
    }

    /** 观察当前文档内所有待入场元素，并按 data 属性写入延迟 */
    observeAll() {
      const nodes = document.querySelectorAll(this.selector);
      for (let i = 0; i < nodes.length; i += 1) {
        const node = nodes[i];
        if (node.getAttribute('data-collar-6-reveal-bound') === 'true') continue;
        node.setAttribute('data-collar-6-reveal-bound', 'true');
        const delay = parseInt(node.getAttribute('data-collar-6-reveal-delay'), 10);
        if (!isNaN(delay) && delay > 0) {
          node.style.transitionDelay = delay + 'ms';
        }
        this.observer.observe(node);
      }
    }

    showAll() {
      const nodes = document.querySelectorAll(this.selector);
      for (let i = 0; i < nodes.length; i += 1) {
        nodes[i].classList.add('is-visible');
      }
    }
  }

  /**
   * 数字滚动计数。
   * 进入视口后用 requestAnimationFrame 跑一次缓动动画（1-(1-p)^4），只执行一次。
   */
  class Collar6CountUp {
    constructor() {
      this.observer = null;
      this.selector = '[data-collar-6-count-up]';
    }

    init() {
      const nodes = document.querySelectorAll(this.selector + ':not([data-collar-6-count-up-bound])');
      if (!nodes.length) return;

      // 不支持 IntersectionObserver 时直接渲染最终数字
      if (!('IntersectionObserver' in window)) {
        for (let i = 0; i < nodes.length; i += 1) {
          this.render(nodes[i], this.getTarget(nodes[i]));
        }
        return;
      }

      this.observer = new IntersectionObserver(
        (entries, observer) => {
          for (let i = 0; i < entries.length; i += 1) {
            const entry = entries[i];
            if (!entry.isIntersecting) continue;
            observer.unobserve(entry.target);
            this.run(entry.target);
          }
        },
        { threshold: 0.4 }
      );

      for (let i = 0; i < nodes.length; i += 1) {
        nodes[i].setAttribute('data-collar-6-count-up-bound', 'true');
        this.observer.observe(nodes[i]);
      }
    }

    getTarget(el) {
      const target = parseInt(el.getAttribute('data-collar-6-count-up'), 10);
      return isNaN(target) ? 0 : target;
    }

    /** 执行一次缓动计数 */
    run(el) {
      const target = this.getTarget(el);
      const duration = parseInt(el.getAttribute('data-collar-6-count-up-duration'), 10) || 1600;
      const reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduceMotion || target <= 0) {
        this.render(el, target);
        return;
      }

      const start = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 4);
        this.render(el, Math.round(target * eased));
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }

    render(el, value) {
      el.textContent = value.toLocaleString() + (el.getAttribute('data-collar-6-count-up-suffix') || '');
    }
  }

  /**
   * FAQ 手风琴。
   * 事件委托绑在容器上，同一容器内一次只展开一项。
   * 展开动画由 CSS 的 grid-template-rows 0fr -> 1fr 完成，JS 只负责切换状态。
   */
  class Collar6Faq {
    constructor(root) {
      this.root = root;
      this.onClick = this.onClick.bind(this);
    }

    init() {
      this.root.addEventListener('click', this.onClick);
    }

    onClick(event) {
      const trigger = event.target.closest('[data-collar-6-faq-trigger]');
      if (!trigger || !this.root.contains(trigger)) return;

      const item = trigger.closest('[data-collar-6-faq-item]');
      if (!item) return;

      const willOpen = item.getAttribute('data-open') !== 'true';
      this.closeAll();
      item.setAttribute('data-open', willOpen ? 'true' : 'false');
      trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    }

    /** 收起容器内所有项 */
    closeAll() {
      const items = this.root.querySelectorAll('[data-collar-6-faq-item]');
      for (let i = 0; i < items.length; i += 1) {
        items[i].setAttribute('data-open', 'false');
        const trigger = items[i].querySelector('[data-collar-6-faq-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }
    }
  }

  let revealInstance = null;

  /** 统一初始化：在空闲时机执行，避免阻塞首屏渲染 */
  function bootstrap() {
    revealInstance = new Collar6Reveal();
    revealInstance.init();
    new Collar6CountUp().init();
    initFaq(document);

    // 主题编辑器里 section 重新渲染后，重新接管新生成的 DOM
    document.addEventListener('shopify:section:load', (event) => {
      if (revealInstance && revealInstance.observer) revealInstance.observeAll();
      new Collar6CountUp().init();
      initFaq(event.target);
    });
  }

  /** 初始化指定根节点下的所有 FAQ 容器 */
  function initFaq(root) {
    const faqRoots = root.querySelectorAll('[data-collar-6-faq]');
    for (let i = 0; i < faqRoots.length; i += 1) {
      if (faqRoots[i].getAttribute('data-collar-6-faq-bound') === 'true') continue;
      faqRoots[i].setAttribute('data-collar-6-faq-bound', 'true');
      new Collar6Faq(faqRoots[i]).init();
    }
  }

  function schedule() {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(bootstrap, { timeout: 800 });
    } else {
      requestAnimationFrame(bootstrap);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }
})();
